import React, { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { trackEvent } from '../../analytics/sml/analytics';
import { TypedMemo } from '../../common/types';
import { appInformation } from '../../../utils/appInformation/appInformation';
import { useUserTokenDecoded } from '../../../redux/slices/appInfo/hooks';
import { useIsNativeEmbed } from '../navigation/hooks/useIsNativeEmbed';
import { handOverUpdate, registerUpdateHost } from '../../appUpdate/appUpdateStore';
import { isHomePath, useQuietHome } from './useQuietHome';
import { useWhatsNewUrl } from './useWhatsNewUrl';
import { useWhatsNew } from './useWhatsNew';
import { useUpdateAvailable } from './useUpdateAvailable';
import { isNewerVersion } from './whatsNewRules';
import { markWhatsNewSeen } from './whatsNewStore';
import { changelogUrl } from './changelogUrl';
import { reloadPageWithoutPanel } from './reloadPage';
import { WhatsNewMode, WhatsNewPanel } from './WhatsNewPanel';

interface Props {
  /** The version of this bundle. Tests set it; the app uses package.json. */
  running?: string;
}

/**
 * Mounts the Music24 web "What's new" panel (spec 2026-10-20 What's new §7, design whats-new
 * a1-a5). The panel opens only on a quiet Home, and the address carries it (`?whatsNew=<version>`):
 * Back closes it. The app has one poll for new builds and one plain prompt
 * (`appUpdate/AppUpdateNotice`). This host tells that prompt to step back when a newer version has
 * published notes and the update panel can show (`appUpdate/appUpdateStore`). The native apps
 * have their own panel, so a native web view shows no panel.
 */
const WhatsNewHostComponent: React.FC<Props> = ({ running = appInformation.version }) => {
  const intl = useIntl();
  const isNative = useIsNativeEmbed();
  const userId = useUserTokenDecoded()?.id ?? null;
  const { pathname } = useLocation();
  const onHome = isHomePath(pathname);
  const quiet = useQuietHome();
  const { urlVersion, open, close } = useWhatsNewUrl();
  const homeVersion = onHome ? urlVersion : null;

  const after = useWhatsNew({
    userId: isNative ? null : userId,
    running,
    locale: intl.locale,
    askAnyway: homeVersion === running,
  });
  const update = useUpdateAvailable({ enabled: !isNative, running, locale: intl.locale, quietHome: quiet });
  const { remote } = update;

  // Take part in the update prompt of the app, so a newer version with notes gets the panel.
  useEffect(() => (isNative ? undefined : registerUpdateHost()), [isNative]);
  useEffect(() => {
    if (remote !== null && update.handover !== null) {
      handOverUpdate(remote.buildId, update.handover);
    }
  }, [remote, update.handover]);

  let mode: WhatsNewMode | null = null;
  if (homeVersion !== null && homeVersion === running && (after.brief?.entries.length ?? 0) > 0) {
    mode = 'after';
  } else if (
    homeVersion !== null &&
    remote !== null &&
    homeVersion === remote.version &&
    isNewerVersion(remote.version, running) &&
    update.notes !== null
  ) {
    mode = 'available';
  }

  // Open by itself only in a quiet moment, once per page view and build.
  const opened = useRef<string | null>(null);
  useEffect(() => {
    if (urlVersion !== null || !quiet) {
      return;
    }
    if (after.shouldOpen && opened.current !== `after:${running}`) {
      opened.current = `after:${running}`;
      open(running);
    } else if (update.prompt === 'panel' && remote !== null && opened.current !== `build:${remote.buildId}`) {
      opened.current = `build:${remote.buildId}`;
      open(remote.version);
    }
  }, [urlVersion, quiet, after.shouldOpen, update.prompt, remote, running, open]);

  // The parameter went away (a button, Esc or Back): store the choice of the panel that showed.
  const shown = useRef<{ mode: WhatsNewMode; buildId: string | null } | null>(null);
  const { markSeen } = after;
  const { hide } = update;
  useEffect(() => {
    if (mode !== null) {
      shown.current = { mode, buildId: remote?.buildId ?? null };
      return;
    }
    if (urlVersion === null && shown.current !== null) {
      const last = shown.current;
      shown.current = null;
      if (last.mode === 'after') {
        markSeen();
      } else {
        hide();
      }
    }
  }, [mode, urlVersion, remote, markSeen, hide]);

  // A parameter that names nothing to show (an old link, an unpublished version) leaves the address.
  const waiting =
    (homeVersion === running && !after.settled) ||
    (remote !== null && homeVersion === remote.version && update.notesState === 'pending');
  useEffect(() => {
    if (homeVersion !== null && mode === null && !waiting) {
      close();
    }
  }, [homeVersion, mode, waiting, close]);

  const reloadToNewVersion = useCallback(() => {
    if (userId !== null && remote !== null) {
      // The person read these notes in the panel: the new build does not show them again.
      markWhatsNewSeen(userId, remote.version);
    }
    reloadPageWithoutPanel();
  }, [userId, remote]);

  const brief = mode === 'after' ? after.brief : update.notes;
  const shownVersion = mode === null ? null : (brief?.version ?? null);
  useEffect(() => {
    if (shownVersion !== null) {
      trackEvent('whats_new_viewed', { version: shownVersion });
    }
  }, [shownVersion]);
  if (mode === null || brief === null) {
    return null;
  }
  return (
    <WhatsNewPanel
      mode={mode}
      brief={brief}
      currentVersion={running}
      changelogHref={changelogUrl(intl.locale, brief.version)}
      onClose={close}
      onReload={reloadToNewVersion}
    />
  );
};

export const WhatsNewHost = TypedMemo(WhatsNewHostComponent);
